import "./truncator.scss";

import { useState, ReactNode } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import cn from "clsx";

import { Alignment, EditAttributes } from "design-system/types/types";
import { IconPlus } from "design-system/components/icons/icon-plus";

export interface TruncatorProps {
  /** Clickable button label when Truncator content is hidden */
  labelHidden?: string;
  /** Clickable button label when Truncator content is visible */
  labelVisible?: string;
  /** Content components */
  children?: ReactNode;
  /** Global alignment class */
  align?: Alignment;
  headerAbove?: boolean;

  editAttributes?: {
    hiddenStateLabel?: EditAttributes;
    visibleStateLabel?: EditAttributes;
  };
}

/**
 *
 * The truncator component allows users to reveal or hide content. This is
 * useful for reducing cognitive load for large sections of content.
 *
 * ## How it Works
 * - Truncator contents are hidden by default
 * - Clicking on an truncator item will reveal its contents
 * - Clicking on an truncator item while open will hide its contents
 *
 * ## Implementation Notes
 * This component utilizes the [Radix
 * Collapsible](https://www.radix-ui.com/docs/primitives/components/collapsible)
 * components
 *
 * - **`data-region: cb__truncator`**
 */
// TODO: Give this component a numeric id
// TODO: **UPDATE HBS Identity Site** with this component
export function Truncator({
  labelHidden = "More Details",
  labelVisible = "Hide Details",
  children,
  align = "wide",
  headerAbove = false,
  editAttributes,
}: TruncatorProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!children) return null;

  function handleClick() {
    setIsOpen(!isOpen);
  }

  const headerClass = headerAbove
    ? "hbs-truncator__header-above"
    : "hbs-truncator__header-below";

  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component-truncator",
    headerClass,
  );

  return (
    <Collapsible.Root
      className={wrapperClass}
      open={isOpen}
      data-region="cb__truncator"
    >
      <Collapsible.Trigger
        className="hbs-truncator__trigger"
        onClick={handleClick}
      >
        <span
          className="hbs-truncator__label"
          {...(isOpen
            ? editAttributes?.visibleStateLabel
            : editAttributes?.hiddenStateLabel)}
        >
          {isOpen ? labelVisible : labelHidden}
        </span>
        <IconPlus className="hbs-truncator__icon" aria-hidden />
      </Collapsible.Trigger>
      <Collapsible.CollapsibleContent className="hbs-truncator__content">
        {children}
      </Collapsible.CollapsibleContent>
      <div className="hbs-truncator-print-only-content">{children}</div>
    </Collapsible.Root>
  );
}
